import axiosInstance, { BASE_API_URL } from './router/axiosInstance';
import { logger } from './logger';

// RT-3: живые позиции этажа — SSE-поток RT-2 с деградацией в батч-polling RT-3.
//
// Схема: подключаемся fetch-стримом к /website/positions/stream (браузерный
// EventSource НЕ умеет Authorization-заголовок — поэтому fetch + ReadableStream;
// БЕЗ credentials:'include' — бэкенд отдаёт CORS `*`, с include браузер
// заблокировал бы ответ, а cookie эндпоинту не нужны). Пока стрим жив —
// события приходят пушем, plus редкий reconcile-снапшот (ушедшие со смены НЕ
// генерят событий — без снапшота метка висела бы вечно). Стрим упал/закрыт
// (в т.ч. плановое серверное завершение по lifetime ~30 мин — там реконнект
// прогоняет auth заново) — переходим на снапшот-polling батч-эндпоинта
// /website/positions (1 запрос/тик вместо легаси N+1) и периодически пробуем
// стрим снова. SSE и polling НИКОГДА не работают одновременно.
//
// Снапшот сознательно БЕЗ since-курсора: каноника ts может отставать от приёма
// до 300с (время замера телефона), строгий курсор терял бы такие кадры
// (докстринг бэкенда load_latest_positions); кадры идут ~раз в 30с — трафик
// полного снапшота ничтожен, а полнота гарантирована.
//
// Требует на бэке REALTIME_ENABLED + HISTORY_READ_UNIFIED (флаги включаются
// вместе с этим фронтом, см. деплой-ранбук) — иначе оба источника отдают 404.

export const POLL_INTERVAL_MS = 5000;       // тик снапшот-polling при упавшем SSE
export const SSE_RETRY_MS = 30000;          // пауза перед новой попыткой стрима
export const RECONCILE_INTERVAL_MS = 30000; // снапшот при ЖИВОМ стриме (уходы со смены)
// Ожидание ЗАГОЛОВКОВ стрима: black-hole коннект (фаервол, застрявший прокси)
// без таймаута вешал бы await fetch на минуты браузерного TCP-таймаута —
// деградация в polling не наступала бы, хотя батч-эндпоинт доступен.
export const STREAM_CONNECT_TIMEOUT_MS = 15000;

// Инкрементальный парсер SSE-потока: скармливаем текстовые чанки (границы
// кадров могут резаться посреди чанка), забираем содержимое data:-строк.
// Comment-кадры (": ping") и служебные поля игнорируются.
export function createSseParser() {
  let buffer = '';
  return function feed(chunk) {
    buffer += chunk;
    const events = [];
    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).replace(/^ /, ''))
        .join('\n');
      if (data) events.push(data);
    }
    return events;
  };
}

function scopeQuery(buildingId, floor) {
  return `building_id=${encodeURIComponent(buildingId)}&floor=${encodeURIComponent(floor)}`;
}

export default function subscribeLivePositions({
  buildingId,
  floor,
  onUsers,
  pollIntervalMs = POLL_INTERVAL_MS,
  sseRetryMs = SSE_RETRY_MS,
  reconcileIntervalMs = RECONCILE_INTERVAL_MS,
  streamConnectTimeoutMs = STREAM_CONNECT_TIMEOUT_MS,
}) {
  let stopped = false;
  let abortController = null;
  let pollTimer = null;
  let reconcileTimer = null;
  let retryTimer = null;
  // Растёт на КАЖДОМ применении данных (SSE-кадр или снапшот): снапшот,
  // за время полёта которого seq изменился, отбрасывается — его данные
  // прочитаны из Mongo раньше уже применённых и откатили бы метку назад
  // (гонка late-response, до 30с неверной позиции; воркфлоу Medium).
  let dataSeq = 0;
  const users = new Map();

  const emit = () => {
    if (!stopped) onUsers([...users.values()]);
  };

  // Кадр/элемент батча -> метка. SSE-payload — нетипизированная граница
  // (Redis-канал адресный, но публиковать туда может кто угодно с доступом к
  // Redis): форма валидируется всегда, а SSE-кадр ОБЯЗАН нести скоуп и
  // совпасть с подпиской (requireScope — штатный publisher RT-1 всегда кладёт
  // building_id/floor, кадр без них = нештатный публикатор, за борт; Codex:
  // опциональная проверка обходилась кадром без полей). Батч-элементы скоупа
  // не несут (он уже в запросе) — для них только форма.
  const applyPosition = (p, { requireScope }) => {
    if (
      !p ||
      !Number.isFinite(p.user_id) ||
      !Number.isFinite(p.x) ||
      !Number.isFinite(p.y) ||
      !Number.isFinite(p.r)
    ) {
      logger.error('live-positions: кадр с невалидной формой пропущен', p);
      return false;
    }
    if (
      requireScope &&
      (Number(p.building_id) !== Number(buildingId) || Number(p.floor) !== Number(floor))
    ) {
      logger.error('live-positions: кадр вне скоупа подписки пропущен', p);
      return false;
    }
    users.set(p.user_id, {
      id: p.user_id,
      coordinates: { x: p.x, y: p.y, radius: p.r },
    });
    return true;
  };

  // Снапшот = истина «кто сейчас на этаже»: полная замена (уход со смены —
  // это ИСЧЕЗНОВЕНИЕ из списка, инкрементальный merge его не увидел бы).
  const snapshot = async () => {
    const startSeq = dataSeq;
    const { data } = await axiosInstance.get(`website/positions?${scopeQuery(buildingId, floor)}`);
    // Пока ответ летел, применились более свежие данные (SSE-кадр, другой
    // снапшот) или пришла отписка — полная замена этим ответом откатила бы
    // метки назад; следующий тик/reconcile возьмёт свежий снапшот.
    if (stopped || dataSeq !== startSeq) return;
    dataSeq += 1;
    users.clear();
    data.positions.forEach((p) => applyPosition(p, { requireScope: false }));
    emit();
  };

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const startPolling = () => {
    if (pollTimer || stopped) return;
    pollTimer = setInterval(() => {
      snapshot().catch((e) => logger.error('live-positions: polling', e));
    }, pollIntervalMs);
  };

  const stopReconcile = () => {
    if (reconcileTimer) {
      clearInterval(reconcileTimer);
      reconcileTimer = null;
    }
  };

  const runStream = async () => {
    const token = localStorage.getItem('access_token');
    // Страховка от потерянной ссылки: старый контроллер (если есть) гасим
    // ДО перезаписи — иначе соединение, брошенное исключением до abort,
    // становилось бы недостижимым навсегда (зомби-стрим, воркфлоу High).
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const controller = abortController;
    // Connect-таймаут: снимается сразу по приходу заголовков.
    const connectTimer = setTimeout(() => controller.abort(), streamConnectTimeoutMs);
    let response;
    try {
      response = await fetch(
        `${BASE_API_URL}/api/v1/website/positions/stream?${scopeQuery(buildingId, floor)}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        }
      );
    } finally {
      clearTimeout(connectTimer);
    }
    try {
      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP ${response.status}`);
      }
      // Стрим жив: polling выключаем, стартовый снапшот берём ПОСЛЕ подписки
      // (события, случившиеся между подпиской и снапшотом, уже в positions —
      // publish RT-1 идёт после записи истории, окно не теряется).
      stopPolling();
      await snapshot();
      reconcileTimer = setInterval(() => {
        snapshot().catch((e) => logger.error('live-positions: reconcile', e));
      }, reconcileIntervalMs);

      const reader = response.body.getReader();
      const decoder = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null;
      const parse = createSseParser();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = typeof value === 'string' ? value : decoder.decode(value, { stream: true });
        for (const data of parse(text)) {
          try {
            if (applyPosition(JSON.parse(data), { requireScope: true })) {
              dataSeq += 1; // применённый кадр «старит» снапшоты в полёте
              emit();
            }
          } catch (e) {
            logger.error('live-positions: битый кадр', e);
          }
        }
      }
      // Сервер закрыл поток (плановый lifetime/рестарт) — наружу как разрыв:
      // цикл выше решит, поллить и когда переподключаться (auth заново).
      throw new Error('SSE-поток закрыт сервером');
    } finally {
      stopReconcile();
      // Выход из runStream ЛЮБЫМ путём = стрим больше не нужен: без abort
      // упавший ПОСЛЕ успешного коннекта снапшот (404 при перекосе флагов,
      // 5xx, сеть) бросал живое SSE-соединение — каждый 30с-ретрай копил
      // зомби, выедая браузерный лимит 6/host и серверный REALTIME_MAX_STREAMS
      // с Redis-пулом до 30-мин lifetime (воркфлоу High + security Medium).
      controller.abort();
    }
  };

  const loop = async () => {
    while (!stopped) {
      try {
        await runStream();
      } catch (e) {
        if (!stopped) logger.error('live-positions: стрим недоступен', e);
      }
      if (stopped) break;
      // Деградация: мгновенный снапшот + регулярный polling до успеха стрима.
      await snapshot().catch((e) => logger.error('live-positions: снапшот', e));
      // Отписка могла прийти во время снапшота — новый retryTimer её уже
      // не достанет (clearTimeout отписки прошёл раньше; воркфлоу Low).
      if (stopped) break;
      startPolling();
      await new Promise((resolve) => {
        retryTimer = setTimeout(resolve, sseRetryMs);
      });
    }
  };
  loop();

  return function unsubscribe() {
    stopped = true;
    if (abortController) abortController.abort();
    stopPolling();
    stopReconcile();
    if (retryTimer) clearTimeout(retryTimer);
  };
}
